import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/providers';
import { authService } from '@/services/authService';
import { toast } from 'sonner';

const profileSchema = Yup.object({
    firstName: Yup.string()
        .min(1, 'First name is required')
        .max(100, 'First name must be less than 100 characters')
        .required('First name is required'),
    lastName: Yup.string()
        .min(1, 'Last name is required')
        .max(100, 'Last name must be less than 100 characters')
        .required('Last name is required'),
    email: Yup.string()
        .email('Invalid email format')
        .required('Email is required'),
});

export function ProfileSettings() {
    const { user, updateProfile } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
        },
        validationSchema: profileSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                // Note: Auth provider updateProfile only handles firstName/lastName
                // For email updates, we need to use the service directly
                if (values.email !== user?.email) {
                    const response = await authService.updateProfile(values);
                    if (response.success) {
                        toast.success(response.message);
                        // Refresh user data after email change
                        window.location.reload(); // Simple approach for now
                    } else {
                        toast.error(response.message || 'Failed to update profile');
                    }
                } else {
                    await updateProfile(values.firstName, values.lastName);
                    toast.success('Profile updated successfully');
                }
            } catch (error: any) {
                toast.error(error.message || 'Failed to update profile');
            } finally {
                setIsLoading(false);
            }
        },
    });

    const hasChanges =
        formik.values.firstName !== (user?.firstName || '') ||
        formik.values.lastName !== (user?.lastName || '') ||
        formik.values.email !== (user?.email || '');

    return (
        <Card className="p-6">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-medium">Profile Information</h3>
                    <p className="text-sm text-muted-foreground">
                        Update your account profile information.
                    </p>
                </div>

                <Separator />

                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                type="text"
                                placeholder="Enter your first name"
                                value={formik.values.firstName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.firstName && formik.errors.firstName ? 'border-destructive' : ''}
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                                <p className="text-sm text-destructive">{formik.errors.firstName}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                type="text"
                                placeholder="Enter your last name"
                                value={formik.values.lastName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.lastName && formik.errors.lastName ? 'border-destructive' : ''}
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                                <p className="text-sm text-destructive">{formik.errors.lastName}</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="Enter your email address"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={formik.touched.email && formik.errors.email ? 'border-destructive' : ''}
                        />
                        {formik.touched.email && formik.errors.email && (
                            <p className="text-sm text-destructive">{formik.errors.email}</p>
                        )}
                        {user?.email !== formik.values.email && (
                            <p className="text-sm text-muted-foreground">
                                Changing your email will require verification of the new address.
                            </p>
                        )}
                    </div>

                    <div className="flex justify-end">
                        <Button
                            type="submit"
                            disabled={isLoading || !hasChanges || !formik.isValid}
                        >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </Card>
    );
}
